// ActivityHeatmap — T-016 Part B. GitHub-style year grid driven by workout
// dates. Each cell is one day; intensity reflects the number of workouts on
// that day. The component receives the dates, not the workout data.

import type { JSX } from "react";
import { useMemo } from "react";
import { useI18n } from "../../i18n";

export interface ActivityHeatmapProps {
  /** ISO date strings ("YYYY-MM-DD") for completed workouts. */
  dates: string[];
  /** Number of weeks to render (default 52 — about a year). */
  weeks?: number;
  /** Anchor date; cells are anchored so that the rightmost column ends here. */
  today?: string;
}

interface Day {
  iso: string;
  count: number;
  level: 0 | 1 | 2 | 3 | 4;
}

function startOfIsoDay(iso: string): Date {
  // Treat as UTC midnight to avoid timezone drift in the grid.
  return new Date(`${iso}T00:00:00Z`);
}

function isoOf(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function levelFor(count: number): Day["level"] {
  if (count <= 0) return 0;
  if (count === 1) return 1;
  if (count === 2) return 2;
  if (count <= 4) return 3;
  return 4;
}

const LEVEL_BG: Record<Day["level"], string> = {
  0: "var(--color-slab)",
  1: "rgba(198,255,61,0.20)",
  2: "rgba(198,255,61,0.45)",
  3: "rgba(198,255,61,0.75)",
  4: "var(--color-lime)",
};

export function ActivityHeatmap({
  dates,
  weeks = 52,
  today,
}: ActivityHeatmapProps): JSX.Element {
  const { t } = useI18n();
  const days: Day[] = useMemo(() => {
    const counts = new Map<string, number>();
    for (const d of dates) {
      counts.set(d, (counts.get(d) ?? 0) + 1);
    }
    const end = today ? startOfIsoDay(today) : startOfIsoDay(new Date().toISOString().slice(0, 10));
    // Find the most recent Sunday on or after `end` so the rightmost column
    // ends with a full week.
    const endDow = end.getUTCDay();
    const offset = (7 - endDow) % 7;
    const last = new Date(end);
    last.setUTCDate(end.getUTCDate() + offset);
    const total = weeks * 7;
    const start = new Date(last);
    start.setUTCDate(last.getUTCDate() - (total - 1));
    const out: Day[] = [];
    for (let i = 0; i < total; i++) {
      const d = new Date(start);
      d.setUTCDate(start.getUTCDate() + i);
      const iso = isoOf(d);
      const count = counts.get(iso) ?? 0;
      out.push({ iso, count, level: levelFor(count) });
    }
    return out;
  }, [dates, weeks, today]);

  if (dates.length === 0) {
    return (
      <p
        className="font-sans text-sm text-[var(--color-ink-mute)]"
        data-testid="heatmap-empty"
      >
        {t("analytics.heatmapEmpty")}
      </p>
    );
  }

  return (
    <div
      data-testid="activity-heatmap"
      className="flex flex-col gap-2"
      aria-label={t("analytics.heatmapLabel")}
    >
      <div
        className="grid gap-[3px]"
        style={{ gridTemplateColumns: `repeat(${weeks}, 1fr)` }}
      >
        {days.map((d) => (
          <div
            key={d.iso}
            title={`${d.iso} — ${d.count}`}
            data-testid="heatmap-cell"
            data-iso={d.iso}
            data-level={d.level}
            style={{
              backgroundColor: LEVEL_BG[d.level],
              aspectRatio: "1 / 1",
              borderRadius: 2,
            }}
          />
        ))}
      </div>
    </div>
  );
}
